import {FC, useMemo} from 'react';
import DefaultLayout from '@/layouts/Default/default';
import AboutHero from '@/components/Hero/AboutHero';
import {
  Heading2,
  Heading3,
  Paragraph,
  ImpactStatement,
  FeatureList,
  Quote,
  KeyBulletList,
} from '@/components/Blog/BlogComponents';
import {Seo} from '@/components/SEO/Seo';
import {pageMetadata} from '@/config/pageMetadata';
import {siteMetadata} from '@/config/seo';

const peoplePoints = [
  {
    icon: 'library_books',
    title: 'Knowledge sharing as a product',
    text: "Our IP is the transfer of know-how. We document, teach, and ship playbooks so context moves faster than projects.",
  },
  {
    icon: 'sports_esports',
    title: 'Gamified growth paths',
    text: 'Everyone gets a tailored growth roadmap. We make learning exponential, fun, and tied to real outcomes.',
  },
  {
    icon: 'schedule',
    title: 'Dedicated teaching time',
    text: '30% of our week is reserved for learning, mentoring, and codifying what we discover.',
  },
];

const craftPoints = [
  {
    icon: 'route',
    title: 'Roadmap-guided improvement',
    text: 'We constantly test new methods, update playbooks, and level up the entire crew.',
  },
  {
    icon: 'category',
    title: 'Cross-disciplinary perspective',
    text: 'Design, engineering, AI, and narrative collide. Rigor meets creativity and produces stand-out work.',
  },
  {
    icon: 'bar_chart',
    title: 'Measuring what matters',
    text: 'Craft equals outcomes. We obsess over metrics that show the work moved the needle.',
  },
];

const outcomesPoints = [
  {
    icon: 'flag',
    title: 'Strategic alignment',
    text: 'Every engagement starts with the business objective and keeps receipts all the way through.',
  },
  {
    icon: 'experiment',
    title: 'Refinement through experimentation',
    text: 'We iterate relentlessly, reducing cost while increasing efficiency for the client.',
  },
  {
    icon: 'robot',
    title: 'Automation as leverage',
    text: 'We automate the monotonous so humans can stay in strategy, creativity, and relationship mode.',
  },
];

const ethosPrinciples = [
  {
    title: 'The ultimate OKR is iteration speed',
    description: 'How quickly we create, test, adapt, and decide drives everything. Every level of the company is measured by how fast we can learn and adjust.',
  },
  {
    title: 'We are paid to play',
    description: "This is supposed to be fun. We treat the work like a game worth winning and make the score transparent so everyone knows how to level up.",
  },
  {
    title: 'Protect focus relentlessly',
    description: 'Asynchronous-first, distraction-free, and focus-driven rituals power the outcomes. Guard the time that lets deep work happen.',
  },
  {
    title: 'Our people are the product',
    description: 'Empowering others is the ultimate investment. Give more than you take and lead with generosity—for teammates and for customers.',
  },
  {
    title: 'Empathy multiplies impact',
    description: 'Operate with intense empathy. Seeing the world from the other side amplifies the products, relationships, and the stories we ship.',
  },
  {
    title: 'Everything is for an outcome',
    description: 'Every action points to a measurable goal. We might not plot the entire journey, but we always have a north star and a clear purpose.',
  },
  {
    title: 'Grow or die',
    description: 'Stagnation kills momentum. Small, consistent improvements compound into exponential progress.',
  },
  {
    title: 'Applied over concepts',
    description: "We don’t learn in theory first—we jump in, try it, then codify what worked. It’s skydiving with a guide, not reading the manual on the ground.",
  },
  {
    title: 'We are asynchronous-first',
    description: 'If it can’t be done async, something is broken. Meetings are for deep 1:1 investment, not status updates.',
  },
  {
    title: 'Our habits and perspectives define us',
    description: "The ‘how’ matters more than the ‘what’. Blended perspectives and intentional habits are how we achieve what others can’t.",
  },
  {
    title: 'Build the plane while flying',
    description: 'Refactor, automate, and improve in-flight. We fix and ship simultaneously.',
  },
  {
    title: 'Automate everything',
    description: 'If the result stays the same, automate it now. Leverage is how we keep speed and quality aligned.',
  },
];

const futureVision = [
  {
    title: 'Agency',
    description: 'Our proving ground. We test new methodologies in the wild and ship results for partners who need leverage now.',
  },
  {
    title: 'Ventures',
    description: 'Spin up products and internal tools that remove friction for the agency and unlock new revenue streams.',
  },
  {
    title: 'Institute',
    description: 'Reinvent talent development. Infinite learners trained on real work, ready to drop into high-stakes teams.',
  },
];

const AboutPage: FC = () => {
  const seo = pageMetadata.about;
  const aboutStructuredData = useMemo(() => {
    const description = seo.description ?? siteMetadata.defaultDescription;
    const canonicalUrl = `${siteMetadata.siteUrl}${seo.canonicalPath ?? '/about'}`;
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'ProfessionalService',
        name: siteMetadata.siteName,
        description,
        url: canonicalUrl,
        areaServed: {
          '@type': 'City',
          name: 'Fayetteville',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Fayetteville',
            addressRegion: 'AR',
            addressCountry: 'US',
          },
        },
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Fayetteville',
          addressRegion: 'AR',
          addressCountry: 'US',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 36.0626,
          longitude: -94.1574,
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: seo.title ?? 'About Turbo Labs',
        description,
        url: canonicalUrl,
        isPartOf: {
          '@type': 'WebSite',
          url: siteMetadata.siteUrl,
          name: siteMetadata.siteName,
        },
      },
    ];
  }, [seo.canonicalPath, seo.description, seo.title]);
  return (
    <DefaultLayout>
      <Seo {...seo} structuredData={aboutStructuredData}/>
      <AboutHero/>
      <div className="min-h-screen w-full bg-white dark:bg-zinc-950">
        <section className="mx-auto w-full max-w-5xl px-4 pt-32 sm:px-6 lg:px-8">
          <span className="text-xs uppercase tracking-[0.55em] text-zinc-500 dark:text-zinc-400">
            Inside Turbo Labs
          </span>
          <h1
            className="mt-6 font-montreal lowercase text-[clamp(3.5rem,8vw,6rem)] font-medium leading-tight text-zinc-900 dark:text-zinc-50">
            hello.
          </h1>
          <Paragraph>
            Today's tech and creative teams are drowning in noise, misaligned work, and slow enablement. Turbo Labs
            exists to break
            that cycle. Through our Institute, Ventures, and Agency, we keep the feedback loop tight: learn fast, ship
            fast, and
            prove outcomes with minimal overhead.
          </Paragraph>
         
        </section>

        <div className="mx-auto w-full max-w-4xl px-4 pb-24 pt-16 sm:px-6 lg:px-8">
          <Heading2
            id="non-traditional-outcomes"
            description="We live in the future. Our culture is built for speed so partners can capture outcomes no traditional model can touch."
          >
            Non-traditional Culture → Non-traditional Outcomes
          </Heading2>
          <Paragraph>
            After years of leading product teams, we built Turbo Labs to move at the actual pace of technology. The
            obsession has
            always been the same: adapt faster, iterate smarter, and keep pushing the boundary of what ships.
          </Paragraph>
          <Paragraph>
            We're operating inside a paradox. Technology compounds in power every quarter, yet most businesses still
            struggle to
            harness it. The gap between what's possible and what actually launches keeps widening.
          </Paragraph>
          <ul
            className="mb-10 mt-10 space-y-3 border-l border-zinc-200 pl-6 text-base leading-relaxed text-zinc-700 dark:border-zinc-800 dark:text-zinc-300">
            <li>The value of exceptional design is still trapped inside mediocre execution.</li>
            <li>Elite creators struggle to translate their craft into business value.</li>
            <li>AI and automation stay buzzwords while tailored application is already transformational.</li>
            <li>Traditional education keeps failing talent while the junior market collapses.</li>
          </ul>
          <Paragraph>
            Turbo Labs is how we transcend that gap. We architect teams, systems, and processes that treat knowledge as
            leverage so
            every engagement compounds.
          </Paragraph>
          <Paragraph>
            The truth is, the world already operates differently. There's a gold rush underway for those who understand
            how to
            wield it—and we intend to stay out ahead.
          </Paragraph>
          <Paragraph>
            We engineered a new operating model during that realization: a company where agency, ventures, and institute
            feed each
            other so talent, tooling, and outcomes accelerate together.
          </Paragraph>

          <Heading3 id="engineered-for-ai" description="We shape systems that survive production—not just the demo.">
            Engineered for the AI Age
          </Heading3>
          <Paragraph>
            We are deep in the curve where capability multiplies overnight. Modern AI and intelligent agents can
            shoulder
            meaningful work—if they’re orchestrated with rigor. Our teams live in the R&amp;D trenches so partners adopt
            proven
            automation, not hype.
          </Paragraph>

          <ImpactStatement
            content="Simply put: we're a non-traditional tech organization built to exponentially grow our impact."/>

          <Heading2 id="people"
                    description="Infinite learners committed to raising the bar for each other and our clients.">
            People
          </Heading2>
          <Paragraph>
            Knowledge is the product. We bring together high-potential individuals who make a 10X impact and create the
            space for
            them to keep leveling up.
          </Paragraph>
          <KeyBulletList heading="How we inject this" description={null} points={peoplePoints}/>

          <Heading2 id="craft" description="Blending rigor and creativity so the work actually stands out.">
            Craft
          </Heading2>
          <Paragraph>
            In a world with infinite paths to a solution, true craftsmanship happens where mastery meets precision. We
            test
            relentlessly and raise the standard every sprint.
          </Paragraph>
          <KeyBulletList heading="How we inject this" description={null} points={craftPoints}/>

          <Heading2 id="outcomes" description="We build case studies, not portfolio fluff.">
            Outcomes
          </Heading2>
          <Paragraph>
            Everything starts with the business objective. We measure success by the revenue unlocked, the cost reduced,
            and the
            teams we put back on offense.
          </Paragraph>
          <KeyBulletList heading="How we inject this" description={null} points={outcomesPoints}/>

          <Quote>
            Turbo Labs aims to get faster as it scales—not slower.
          </Quote>

          <section className="mt-28">
            <span className="text-xs uppercase tracking-[0.55em] text-zinc-500 dark:text-zinc-400">Mission</span>
            <p
              className="mt-6 font-montreal text-[clamp(2.8rem,6vw,4.8rem)] leading-tight text-zinc-900 dark:text-zinc-50">
              We unite business and tech creators to accelerate extraordinary outcomes through bold creativity and
              cutting-edge
              technology.
            </p>
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-zinc-700 dark:text-zinc-300">
              Share what we learn and feed it back into the work. Reach new heights together, faster than our peers.
              Forge ventures
              that reset how industries operate.
            </p>
          </section>

          <Heading2 id="ethos" description="Principles that keep us sharp while the world speeds up.">
            Our Ethos
          </Heading2>
          <Paragraph>
            These principles resist the gravitational pull of busywork. They keep us learning faster than the market and
            make
            sure the work stays human.
          </Paragraph>
          <ol className="mt-12 space-y-10">
            {ethosPrinciples.map((principle, index) => (
              <li key={principle.title} className="flex flex-col gap-4">
                <div className="text-sm uppercase tracking-[0.4em] text-zinc-400 dark:text-zinc-500">
                  {String(index + 1).padStart(2, '0')}
                </div>
                <div>
                  <h3 className="font-montreal text-2xl lowercase leading-tight text-zinc-900 dark:text-zinc-50">
                    {principle.title}
                  </h3>
                  <p className="mt-3 max-w-3xl text-base leading-relaxed text-zinc-700 dark:text-zinc-300">
                    {principle.description}
                  </p>
                </div>
              </li>
            ))}
          </ol>

          <Heading2 id="future" description="Where we're steering Turbo Labs over the next five years.">
            Turbo in 5 Years
          </Heading2>
          <Paragraph>
            We're building a self-reinforcing ecosystem: the agency proves the playbooks, ventures package the IP, and
            the
            institute cultivates the next wave of builders.
          </Paragraph>
          <FeatureList items={futureVision}/>

          <ImpactStatement
            content="We invite partners who want to move at this pace. Let's accelerate outcomes together."/>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default AboutPage;
